import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  BookOpen,
  Check,
  Copy,
  Library,
  MessageCircle,
  Search,
  Settings,
  Sparkles,
  UserPlus,
  Users,

  Download,
  Linkedin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import CircularProgress from "@/components/common/CircularProgress";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  checkComunidadAdminAccess,
  comunidadAdapter,
  type ComunidadTrayectoria,
  type ComunidadCalendarEvent,
  type FormacionParticipacion,
} from "@/hooks/api/comunidad";
import { usePsychologistData } from "@/hooks/usePsychologistData";
import apiCoreAdapter from "@/hooks/apiCoreAdapter";
import { timezoneToCountryCode } from "@/utils/timezoneToCountry";
import { isoToZoneParts } from "@/components/psychologist/calendar/timeZoneHelpers";
import { buildGmailComposeUrl } from "@/pages/psychologist/tabs/patients/components/PatientTable";
import { useToast } from "@/hooks/useToast";
import { useComunidadContent } from "./hooks/useComunidadContent";
import { blockAgendaForFormacion } from "./formacionBlock";
import { isHolidayFullyBlocked, sessionsOnHoliday } from "./holidaySessions";
import { loadFeriadosTrabaja } from "@/utils/holidayDecisions";
import { HolidaySessionsDialog, HolidaySessionsInfo } from "@/components/psychologist/calendar/HolidaySessionsDialog";
import {
  countryNameFromTimezone,
  fetchPublicHolidays,
  HOLIDAY_NOTICE_DAYS,
  holidaysWithinDays,
  UpcomingHoliday,
} from "@/utils/holidays";
import { FormacionRatingCard } from "./components/FormacionRatingCard";
import {
  downloadCertificadoFormacion,
  downloadCertificadoFormacionImagen,
  downloadConstanciaDictado,
  formatCertificadoDate,
  linkedinPostUrl,
} from "./certificadoFormacionPdf";
import { CertificadoMiniatura } from "./components/CertificadoMiniatura";
import type { FormacionCertificado, FormacionDictada } from "@/hooks/api/comunidad";
import { toastUtils } from "@/hooks/useToast";
import { htmlToPlainText, isRichHtml } from "./richText";
import { activityTracker } from "@/utils/activityTracker";
import { useIsPwaApp } from "@/pwa/PwaModeContext";

const BIBLIOTECA_COMUNITARIA_URL = "https://drive.google.com/drive/folders/1pzRWsrQS60kWwaIpWN7eX_Cxo7Mt_Tvx";

/** Link de "Anotarme"/"Agendar": abre Google Calendar con el evento ya armado, con los datos
 *  cargados en el panel de admin (título, edición, descripción, Meet, duración). */
function buildGoogleCalendarLink(ev: ComunidadCalendarEvent): string | null {
  if (!ev.startsAt) return null;
  const toGcal = (d: Date) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  const start = new Date(ev.startsAt);
  const end = new Date(start.getTime() + ev.durationMinutes * 60_000);
  const prefix = ev.type === "formacion" ? "🎓" : "☕";
  const text = ev.edition ? `${prefix} ${ev.title} (${ev.edition}° edición)` : `${prefix} ${ev.title}`;
  const details = [ev.description, ev.meetUrl ? `Unite por Google Meet: ${ev.meetUrl}` : ""]
    .filter(Boolean)
    .join("\n\n");
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text,
    dates: `${toGcal(start)}/${toGcal(end)}`,
    details,
  });
  return `https://calendar.google.com/calendar/render?${params}`;
}

/** Mensaje que la psicóloga copia y le manda a quien quiera recomendar ("Recomendá a un/a colega"). */
const REFERRAL_MESSAGE = `Si te interesa sumarte al equipo de Psi Mammoliti, te invitamos a completar el proceso de selección. Es un proceso a tu propio ritmo. Podés pausarlo y retomarlo cuando quieras, tu progreso queda guardado.
Para comenzar, ingresá a: https://quierosumarme.psimammoliti.com

¡Te esperamos!

Equipo de Psi Mammoliti`;

/** Tiempo desde el alta, en años y meses completos ("1 año y 10 meses"), más un aviso
 *  cuando faltan 2 meses o menos para cumplir el siguiente año. */
function describeTenure(joinedAtIso: string, now = new Date()) {
  const joined = new Date(joinedAtIso);
  if (Number.isNaN(joined.getTime())) return null;
  let months = (now.getFullYear() - joined.getFullYear()) * 12 + (now.getMonth() - joined.getMonth());
  if (now.getDate() < joined.getDate()) months -= 1;
  months = Math.max(0, months);
  const years = Math.floor(months / 12);
  const rest = months % 12;
  const yearsLabel = years > 0 ? `${years} ${years === 1 ? "año" : "años"}` : "";
  const monthsLabel = rest > 0 ? `${rest} ${rest === 1 ? "mes" : "meses"}` : "";
  const label = [yearsLabel, monthsLabel].filter(Boolean).join(" y ") || "Menos de un mes";
  const nextYears = years + 1;
  const almost = rest >= 10
    ? `¡Ya casi cumplís ${nextYears === 1 ? "un año" : `${nextYears} años`} con nosotros! 🎉`
    : null;
  return { label, almost };
}


/** Convierte un instante UTC a cómo se le muestra a UNA psicóloga puntual: día, mes y hora ya
 *  en SU huso horario (no el de Buenos Aires donde se agendó el evento). */
function formatEventForViewer(startsAtUtc: string, timezone: string) {
  const { date, time } = isoToZoneParts(startsAtUtc, timezone);
  const [y, m, d] = date.split("-").map(Number);
  const localDate = new Date(y, (m || 1) - 1, d || 1);
  const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  const weekday = capitalize(new Intl.DateTimeFormat("es-AR", { weekday: "long" }).format(localDate));
  const month = capitalize(new Intl.DateTimeFormat("es-AR", { month: "short" }).format(localDate).replace(".", ""));
  return {
    day: String(d).padStart(2, "0"),
    month,
    meta: `${weekday} · ${time} hs (tu hora: ${describeTimezone(timezone, new Date(startsAtUtc))})`,
  };
}

/** Nombre explícito de la zona horaria cargada en el perfil: "Buenos Aires, GMT-3". El GMT se
 *  calcula en la fecha del evento (no hoy), por si esa fecha cae del otro lado de un cambio de
 *  horario de verano. Si el runtime no soporta `shortOffset`, queda solo la ciudad. */
function describeTimezone(timezone: string, at: Date): string {
  const city = timezone.split("/").pop()?.replace(/_/g, " ") || timezone;
  try {
    const offset = new Intl.DateTimeFormat("es-AR", { timeZone: timezone, timeZoneName: "shortOffset" })
      .formatToParts(at)
      .find((p) => p.type === "timeZoneName")?.value;
    return offset ? `${city}, ${offset}` : city;
  } catch {
    return city;
  }
}


// "Supervisión" y "Feriado" ocultos por ahora: no hay supervisiones activas todavía, y los
// feriados ya tienen su propio cartel de aviso más abajo — no hace falta el chip acá.
const CALENDAR_TYPES = [
  { key: "formacion", label: "Formación", color: "#8475B3" },
  { key: "cafecito", label: "Cafecito", color: "#F188A6" },
] as const;

const calendarBorderColor: Record<"cafecito" | "formacion" | "supervision" | "feriado", string> = {
  cafecito: "#F188A6",
  formacion: "#8475B3",
  supervision: "#4A7A1E",
  feriado: "#3898EC",
};

export const ComunidadContent = () => {
  const navigate = useNavigate();
  // En la app mobile no existen Home, Agenda ni el panel de admin: se ocultan sus accesos.
  const isPwaApp = useIsPwaApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const { content, loading, error: contentError } = useComunidadContent();
  const [canSeeAdmin, setCanSeeAdmin] = useState(false);

  // `?destacar=formaciones` (lo manda el pop-up de lanzamiento "Quiero saber más"): baja hasta
  // el calendario de formaciones y lo resalta unos segundos. El param se consume al usarlo
  // para que un refresh o el botón "atrás" no repitan el resaltado.
  const calendarSectionRef = useRef<HTMLDivElement>(null);
  const [highlightFormaciones, setHighlightFormaciones] = useState(false);

  // Métricas de Actividad (funnel comunidad_inscripcion): el calendario de formaciones está siempre
  // visible en esta pantalla, así que se cuenta UNA vez por visita, cuando termina de cargar.
  const calendarViewedRef = useRef(false);
  useEffect(() => {
    if (loading || calendarViewedRef.current) return;
    calendarViewedRef.current = true;
    activityTracker.trackAction("comunidad_calendar_viewed");
  }, [loading]);

  useEffect(() => {
    if (searchParams.get("destacar") !== "formaciones") return;
    const el = calendarSectionRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightFormaciones(true);
    const next = new URLSearchParams(searchParams);
    next.delete("destacar");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // El resaltado no tiene timer: dura hasta que la psicóloga hace algo (un click o una tecla,
  // en cualquier parte) o sale de la pantalla (el componente se desmonta y todo se limpia).
  // Los listeners solo apagan el estado, no frenan el evento — clickear una novedad igual navega.
  useEffect(() => {
    if (!highlightFormaciones) return;
    const stop = () => setHighlightFormaciones(false);
    document.addEventListener("pointerdown", stop);
    document.addEventListener("keydown", stop);
    return () => {
      document.removeEventListener("pointerdown", stop);
      document.removeEventListener("keydown", stop);
    };
  }, [highlightFormaciones]);
  // La novedad del ciclo de formaciones es contenido editable del admin (sin id fijo): se
  // reconoce por el título. Cuando está, su cuadro en "Institucional" lleva un borde más
  // oscuro para destacarla siempre, no solo mientras dura el resaltado del pop-up.
  const hasFormacionesNews = (content?.institutionalNews ?? []).some((n) => /ciclo de formaciones/i.test(n.title));
  const psychologist = usePsychologistData();
  const viewerTimezone = psychologist?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Argentina/Buenos_Aires";
  const { user } = useAuth();
  const { showSuccess, showInfo, showWarning } = useToast();

  // "Anotarme" a una formación: antes de abrir Google Calendar se bloquea la agenda en el
  // horario del evento (ver formacionBlock.ts). Si ya hay una sesión agendada a esa hora, no
  // se anota y se avisa en un cartel.
  const [enrollingEventId, setEnrollingEventId] = useState<string | null>(null);
  const [sessionConflict, setSessionConflict] = useState<{ title: string; date: string; time: string } | null>(null);

  const handleAnotarme = async (
    e: ReactMouseEvent<HTMLAnchorElement>,
    ev: ComunidadCalendarEvent,
    agendaLink: string,
  ) => {
    // Solo las formaciones bloquean agenda; "Agendar" (cafecito) sigue siendo un link común.
    if (ev.type !== "formacion") return;
    e.preventDefault();
    if (enrollingEventId) return;
    setEnrollingEventId(ev.id);
    // La pestaña se abre ya, dentro del gesto del click: si se abriera después del await, el
    // navegador la bloquearía como popup. Después se le carga el link (o se cierra si no se anota).
    const tab = window.open("", "_blank");
    if (tab) tab.opener = null;
    try {
      const result = await blockAgendaForFormacion(ev);
      if (result.status === "conflict") {
        tab?.close();
        const { date, time } = isoToZoneParts(result.sessionStartIso, viewerTimezone);
        const [, month, day] = date.split("-");
        setSessionConflict({ title: ev.title, date: `${day}/${month}`, time });
        return;
      }
      if (result.status === "error") {
        showWarning("No pudimos bloquear tu agenda en el horario de la formación. Revisá tu disponibilidad por las dudas.");
      } else if ((result.status === "blocked" || result.status === "already_blocked") && ev.startsAt) {
        // Confirmación visible: sin esto, anotarse bien y no hacer nada se ven igual desde afuera.
        const from = isoToZoneParts(ev.startsAt, viewerTimezone);
        const to = isoToZoneParts(new Date(new Date(ev.startsAt).getTime() + ev.durationMinutes * 60_000).toISOString(), viewerTimezone);
        const [, month, day] = from.date.split("-");
        const when = `el ${day}/${month} de ${from.time} a ${to.time} hs`;
        if (result.status === "blocked") showSuccess(`Bloqueamos tu agenda ${when} para que no te agenden sesiones en ese horario.`);
        else showInfo(`Tu agenda ya estaba bloqueada ${when}.`);
      }
      // Registra la inscripción para el listado del panel de admin (si falla, el link se abre igual).
      if (ev.formacionKey && ev.edition) {
        comunidadAdapter
          .inscribirFormacion(ev.formacionKey, ev.edition)
          // Métricas de Actividad: la inscripción se registró con éxito.
          .then(() => activityTracker.trackAction("comunidad_enrollment"))
          .catch(() => undefined);
      }
      if (tab) tab.location.href = agendaLink;
      else window.open(agendaLink, "_blank", "noopener");
    } finally {
      setEnrollingEventId(null);
    }
  };

  const sugerirRecursoUrl = buildGmailComposeUrl("administracion@psimammoliti.com", user?.email, {
    subject: "Sugerencia para la Biblioteca Comunitaria",
  });
  const proponerTemaUrl = buildGmailComposeUrl("administracion@psimammoliti.com", user?.email, {
    subject: "Mi aporte para la Comunidad",
  });
  const now = new Date();
  // La formación del mes la calcula el backend a partir del calendario cargado en el panel:
  // "Calificá" se abre cuando termina su 1ª edición y se cierra a fin de ese mes.
  const currentFormacion = content?.currentFormacion ?? null;
  const showCalifica =
    !!currentFormacion &&
    now >= new Date(currentFormacion.calificaFrom) &&
    now <= new Date(currentFormacion.calificaUntil);
  // ¿Esta psicóloga participó de la formación (≥75% del encuentro, según el informe de Meet
  // que se sube en el panel de admin)? Mientras no se sepa, o si falla, no se muestra nada.
  // Solo en local se puede forzar para ver el diseño con `?preview=formacion`.
  const forcePreviewFormacion =
    process.env.NODE_ENV === "development" &&
    new URLSearchParams(window.location.search).get("preview") === "formacion";
  const [participacion, setParticipacion] = useState<FormacionParticipacion | null>(null);
  const currentFormacionKey = currentFormacion?.key;
  useEffect(() => {
    let cancelled = false;
    if (!currentFormacionKey) {
      setParticipacion(null);
      return;
    }
    comunidadAdapter
      .getFormacionParticipacion(currentFormacionKey)
      .then((data) => { if (!cancelled) setParticipacion(data); })
      .catch(() => { if (!cancelled) setParticipacion(null); });
    return () => { cancelled = true; };
  }, [currentFormacionKey]);
  const participatedInFormacion = !!currentFormacion && (forcePreviewFormacion || !!participacion?.participated);
  // Certificado que se está descargando (por formación) y el que se está compartiendo en
  // LinkedIn: puede ser el de la formación del mes o uno de "Tus certificados".
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
  const [certificado, setShareCertificado] = useState<FormacionCertificado | null>(null);
  const linkedinOpen = certificado !== null;
  const setLinkedinOpen = (open: boolean) => {
    if (!open) setShareCertificado(null);
  };
  // "Tus certificados": las formaciones anteriores en las que participó, para que un
  // certificado no se pierda cuando pasa el mes de su formación.
  const [misCertificados, setMisCertificados] = useState<FormacionCertificado[]>([]);
  useEffect(() => {
    let cancelled = false;
    comunidadAdapter
      .listCertificados()
      .then((data) => { if (!cancelled) setMisCertificados(data); })
      .catch(() => { if (!cancelled) setMisCertificados([]); });
    return () => { cancelled = true; };
  }, []);
  // Formaciones que dictó (el backend devuelve vacío si no dictó ninguna). Quien dictó al
  // menos una tiene la sección "Formaciones" fija debajo de "Tu trayectoria", con sus
  // certificados de dictado y, si además participó de la formación del mes, su certificado de
  // participación y "Calificá" ahí mismo. Igual en la web y en la app mobile.
  const [misDictadas, setMisDictadas] = useState<FormacionDictada[]>([]);
  useEffect(() => {
    let cancelled = false;
    comunidadAdapter
      .listDictadas()
      .then((data) => { if (!cancelled) setMisDictadas(data); })
      .catch(() => { if (!cancelled) setMisDictadas([]); });
    return () => { cancelled = true; };
  }, []);
  const dictoFormaciones = misDictadas.length > 0;
  const [todosOpen, setTodosOpen] = useState(false);
  // En la tarjeta se ve el más nuevo (la lista ya viene de la más nueva a la más vieja).
  const ultimoCertificado = misCertificados[0] ?? null;
  // "Tus certificados" (tarjeta sola): para quien NO fue a la formación del mes pero tiene
  // certificados anteriores — el más nuevo + "Ver todos" (lista completa en una ventana, solo
  // con descarga). Quien fue a la del mes lo tiene como "Ver todos tus certificados" en su
  // tarjeta, para no repetir la misma miniatura.
  const tusCertificadosCard = ultimoCertificado ? (
    <div className="flex flex-col gap-3.5 rounded-[20px] bg-white p-4 shadow-[0_6px_20px_rgba(109,87,188,0.08)] md:p-6">
      <CertificadoMiniatura certificado={ultimoCertificado} />
      <div className="flex flex-grow flex-col gap-1">
        <span className="text-xs font-bold uppercase tracking-wide text-[#8475B3]">Tus certificados</span>
        <span className="text-[15px] font-semibold leading-snug text-[#2D2049]">
          {misCertificados.length === 1
            ? "Tenés 1 certificado"
            : `Tenés ${misCertificados.length} certificados`}
        </span>
        <span className="text-[13px] text-gray-500">Acá se guardan todos, para que los descargues cuando quieras</span>
      </div>
      <Button
        variant="outline"
        size="sm"
        className="self-start border-[#C9BCE8] text-[#665090]"
        onClick={() => setTodosOpen(true)}
      >
        Ver todos
      </Button>
    </div>
  ) : null;

  // "Descargar" baja el PDF; en la ventana de LinkedIn baja la imagen (PNG), que LinkedIn
  // muestra como foto en la publicación.
  const handleDownloadCertificado = async (
    cert: FormacionCertificado | null | undefined,
    formato: "pdf" | "imagen" = "pdf",
  ) => {
    if (!cert) return;
    setDownloadingKey(cert.formacionKey);
    try {
      await (formato === "imagen" ? downloadCertificadoFormacionImagen(cert) : downloadCertificadoFormacion(cert));
      // Métricas de Actividad: certificado descargado.
      activityTracker.trackAction("comunidad_certificado_descargado");
    } catch {
      toastUtils.error("No pudimos armar tu certificado. Probá de nuevo en un rato.");
    } finally {
      setDownloadingKey(null);
    }
  };

  const [downloadingDictadoKey, setDownloadingDictadoKey] = useState<string | null>(null);
  const handleDownloadDictado = async (f: FormacionDictada) => {
    setDownloadingDictadoKey(f.formacionKey);
    try {
      await downloadConstanciaDictado(f.constancia);
      activityTracker.trackAction("comunidad_constancia_dictado_descargada");
    } catch {
      toastUtils.error("No pudimos armar el certificado. Probá de nuevo en un rato.");
    } finally {
      setDownloadingDictadoKey(null);
    }
  };
  // Una tarjeta por formación dictada: su certificado de dictado + acceso al historial
  // ("Mis formaciones dictadas": ediciones, asistentes y devoluciones). Misma forma que la
  // tarjeta "Tu certificado" (en escritorio, miniatura a la izquierda) para que se alineen.
  const renderDictadasCards = () =>
    misDictadas.map((f) => {
      const asistentes = f.ediciones.reduce((n, e) => n + (e.asistentes ?? 0), 0);
      const sinCargar = f.ediciones.every((e) => e.asistentes === null);
      return (
        <div
          key={f.formacionKey}
          data-formacion-dictada={f.formacionKey}
          className="flex flex-col gap-4 rounded-[20px] bg-white p-4 shadow-[0_6px_20px_rgba(109,87,188,0.08)] md:p-6 xl:col-start-1 xl:flex-row xl:items-center xl:gap-5 xl:py-5"
        >
          <CertificadoMiniatura certificado={f.constancia} variant="dictado" className="xl:w-[60%] xl:shrink-0" />
          <div className="flex flex-1 flex-col gap-3">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-bold uppercase tracking-wide text-[#8475B3]">Formación que dictaste</span>
              <span className="text-[15px] font-semibold leading-snug text-[#2D2049]">{f.title}</span>
              <span className="text-[13px] text-gray-500">
                {f.ediciones.length === 1 ? "1 edición" : `${f.ediciones.length} ediciones`}
                {!sinCargar && ` · ${asistentes} ${asistentes === 1 ? "asistente" : "asistentes"}`}
              </span>
            </div>
            <div className="flex flex-col gap-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full whitespace-nowrap border-[#C9BCE8] text-[#665090]"
                disabled={downloadingDictadoKey !== null}
                onClick={() => handleDownloadDictado(f)}
              >
                <Download size={15} className="mr-1.5 shrink-0" />
                {downloadingDictadoKey === f.formacionKey ? "Armando…" : "Descargar certificado"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-auto p-0 text-xs font-semibold text-[#665090] hover:bg-transparent"
                onClick={() => navigate("/comunidad/dictadas")}
              >
                Ver historial y devoluciones →
              </Button>
            </div>
          </div>
        </div>
      );
    });

  // Tu certificado (formación del mes) + "Calificá": en su sección para todas, o dentro de
  // "Formaciones" para quien dictó alguna.
  const renderParticipacionGrid = () => (
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-[7fr_4fr]">
      {/* Tu certificado: miniatura + Descargar + Compartir en LinkedIn (PDF armado en
          el navegador con los datos que manda el backend solo a quien participó). */}
      {participacion?.certificado && (
        <div className="flex flex-col gap-4 rounded-[20px] bg-white p-4 shadow-[0_6px_20px_rgba(109,87,188,0.08)] md:p-6 xl:flex-row xl:items-center xl:gap-5 xl:py-5">
          {/* En escritorio, miniatura a la izquierda y texto + botones a la derecha:
              así la tarjeta queda de la misma altura que "Calificá". */}
          <CertificadoMiniatura certificado={participacion.certificado} className="xl:w-[60%] xl:shrink-0" />
          <div className="flex flex-1 flex-col gap-3">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-bold uppercase tracking-wide text-[#8475B3]">Tu certificado</span>
              <span className="text-[15px] font-semibold leading-snug text-[#2D2049]">Certificado de participación</span>
              <span className="text-[13px] text-gray-500">
                Descargalo en PDF y compartí tu logro en LinkedIn
              </span>
            </div>
            {/* Los dos botones repartidos en partes iguales, ocupando todo el ancho. En escritorio
                ancho (xl) la miniatura va al costado y la columna queda angosta: uno debajo del otro. */}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 xl:grid-cols-1">
              <Button
                variant="outline"
                size="sm"
                className="w-full whitespace-nowrap border-[#C9BCE8] text-[#665090]"
                disabled={downloadingKey !== null}
                onClick={() => handleDownloadCertificado(participacion.certificado)}
              >
                <Download size={15} className="mr-1.5 shrink-0" />
                {downloadingKey === participacion.certificado.formacionKey ? "Armando…" : "Descargar"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full whitespace-nowrap border-[#C9BCE8] text-[#665090]"
                aria-label="Compartir en LinkedIn"
                onClick={() => setShareCertificado(participacion.certificado)}
              >
                <Linkedin size={15} className="mr-1.5 shrink-0" />
                Compartir
              </Button>
            </div>
            {/* Los de formaciones anteriores: solo si tiene más de uno (si no, sería el mismo). */}
            {misCertificados.length > 1 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-auto self-center p-0 text-xs font-semibold text-[#665090] hover:bg-transparent"
                onClick={() => setTodosOpen(true)}
              >
                Ver todos tus certificados →
              </Button>
            )}
          </div>
        </div>
      )}
      <FormacionRatingCard
        formacionKey={currentFormacion?.key ?? ""}
        formacionTitle={currentFormacion?.title ?? ""}
        open={showCalifica || forcePreviewFormacion}
        initialRating={participacion?.calificacion ?? null}
      />
    </div>
  );

  // Chips de tipo de evento — funcionan como filtro multi-selección: todos activos por
  // default (se ve todo), clickear uno lo saca/pone de la vista del calendario.
  const [activeCalendarTypes, setActiveCalendarTypes] = useState<Set<(typeof CALENDAR_TYPES)[number]["key"]>>(
    () => new Set(CALENDAR_TYPES.map((t) => t.key)),
  );
  const toggleCalendarType = (key: (typeof CALENDAR_TYPES)[number]["key"]) => {
    setActiveCalendarTypes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const calendarEvents = content?.calendarEvents ?? [];
  const filteredCalendarEvents = calendarEvents.filter((ev) => activeCalendarTypes.has(ev.type));

  // Banner de cumpleaños / aniversario — solo se muestra el día exacto, comparando mes y día
  // en la timezone de la psicóloga (no la del server) contra su fecha de nacimiento y su
  // fecha de alta real (`created_at` del perfil). Nunca ambos a la vez: cumpleaños gana.
  const [celebration, setCelebration] = useState<{ type: "birthday" | "anniversary"; years?: number } | null>(null);

  useEffect(() => {
    checkComunidadAdminAccess().then(setCanSeeAdmin);
  }, []);

  useEffect(() => {
    let cancelled = false;
    apiCoreAdapter.psychologistProfile().then((resp) => {
      if (cancelled) return;
      const { date: todayDate } = isoToZoneParts(new Date().toISOString(), viewerTimezone);
      const [todayY, todayM, todayD] = todayDate.split("-").map(Number);

      const isSameMonthDay = (iso?: string) => {
        if (!iso) return null;
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return null;
        return { match: d.getUTCMonth() + 1 === todayM && d.getUTCDate() === todayD, year: d.getUTCFullYear() };
      };

      const birthMatch = isSameMonthDay(resp?.birthdate);
      if (birthMatch?.match) {
        setCelebration({ type: "birthday" });
        return;
      }
      const joinMatch = isSameMonthDay(resp?.created_at);
      if (joinMatch?.match) {
        const years = todayY - joinMatch.year;
        if (years > 0) {
          setCelebration({ type: "anniversary", years });
          return;
        }
      }
      setCelebration(null);
    });
    return () => { cancelled = true; };
  }, [viewerTimezone]);

  // "Tu trayectoria" — números reales de esta psicóloga (sesiones, procesos, antigüedad).
  const [trayectoria, setTrayectoria] = useState<ComunidadTrayectoria | null>(null);
  const [trayectoriaLoading, setTrayectoriaLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    comunidadAdapter
      .getTrayectoria()
      .then((data) => { if (!cancelled) setTrayectoria(data); })
      .catch(() => { if (!cancelled) setTrayectoria(null); })
      .finally(() => { if (!cancelled) setTrayectoriaLoading(false); });
    return () => { cancelled = true; };
  }, []);
  const tenure = trayectoria ? describeTenure(trayectoria.joinedAt) : null;
  const formatCount = (n: number) => n.toLocaleString("es-AR");

  // Pop-up de "Recomendá a un/a colega" — mensaje listo para copiar y mandar.
  const [referralOpen, setReferralOpen] = useState(false);
  const [referralCopied, setReferralCopied] = useState(false);
  const copyReferralMessage = async () => {
    try {
      await navigator.clipboard.writeText(REFERRAL_MESSAGE);
      setReferralCopied(true);
      window.setTimeout(() => setReferralCopied(false), 2500);
    } catch {
      setReferralCopied(false);
    }
  };

  // Alerta de feriado — feriados reales del país de la psicóloga (derivado de su timezone),
  // vía la API pública de Nager.Date. Si no hay país detectable o no hay ninguno próximo, no
  // se muestra nada (nunca un feriado de ejemplo).
  const [upcomingHolidays, setUpcomingHolidays] = useState<UpcomingHoliday[]>([]);

  useEffect(() => {
    let cancelled = false;
    const countryCode = timezoneToCountryCode(viewerTimezone);
    if (!countryCode) {
      setUpcomingHolidays([]);
      return;
    }
    // Misma ventana y mismas reglas que el aviso de la Agenda: desde hoy (en su zona horaria)
    // hasta 14 días, sin los feriados en los que ya dijo "Voy a trabajar" o ya bloqueó el día.
    fetchPublicHolidays(countryCode)
      .then((all) => holidaysWithinDays(all, HOLIDAY_NOTICE_DAYS, viewerTimezone))
      .then(async (holidays) => {
        if (holidays.length === 0) return holidays;
        const trabaja = await loadFeriadosTrabaja();
        const resueltos = await Promise.all(
          holidays.map((h) =>
            trabaja.has(h.date) ? true : isHolidayFullyBlocked(h.date, viewerTimezone).catch(() => false),
          ),
        );
        return holidays.filter((_, i) => !resueltos[i]);
      })
      .then((holidays) => { if (!cancelled) setUpcomingHolidays(holidays); })
      .catch(() => { if (!cancelled) setUpcomingHolidays([]); });
    return () => { cancelled = true; };
  }, [viewerTimezone]);

  const holidayNames = upcomingHolidays.map((h) => h.localName);
  const holidayCountry = countryNameFromTimezone(viewerTimezone) ?? "tu país";
  const holidayMessage =
    holidayNames.length === 0
      ? null
      : holidayNames.length === 1
        ? `Se viene ${holidayNames[0]} en ${holidayCountry}`
        : `Se vienen ${holidayNames.slice(0, -1).join(", ")} y ${holidayNames[holidayNames.length - 1]} en ${holidayCountry}`;

  // "Bloquear mi agenda" abre el panel de bloqueo con el feriado más próximo ya cargado
  // (fecha + motivo). Si hay varios en la ventana, va al primero; la psicóloga puede
  // cambiar la fecha en el panel.
  const nextHoliday = [...upcomingHolidays].sort((a, b) => a.date.localeCompare(b.date))[0];
  // Antes de abrir el panel se revisa si ese día (en SU zona horaria) tiene sesiones: si las
  // tiene, no se deja bloquear y se muestra la lista + la política. Nunca toca las sesiones.
  const [checkingHoliday, setCheckingHoliday] = useState(false);
  const [holidaySessions, setHolidaySessions] = useState<HolidaySessionsInfo | null>(null);
  const handleBlockHoliday = async () => {
    if (!nextHoliday || checkingHoliday) return;
    // Métricas de Actividad: tocó "Bloquear mi agenda" en el aviso de feriado.
    activityTracker.trackAction("feriado_bloquear");
    setCheckingHoliday(true);
    try {
      const sessions = await sessionsOnHoliday(nextHoliday.date, viewerTimezone);
      if (sessions.length > 0) {
        setHolidaySessions({ date: nextHoliday.date, name: nextHoliday.localName, sessions });
        activityTracker.trackAction("feriado_sesiones_vistas");
        return;
      }
      navigate(blockHolidayUrl);
    } catch {
      showWarning("No pudimos revisar tu agenda de ese día. Probá de nuevo en un rato.");
    } finally {
      setCheckingHoliday(false);
    }
  };
  const blockHolidayUrl = nextHoliday
    ? `/calendar?${new URLSearchParams({ config: "block", date: nextHoliday.date, reason: `Feriado: ${nextHoliday.localName}` })}`
    : "/calendar?config=block";

  return (
    <div className="min-h-full w-full bg-[#E6DBFA]">
      <div className="mx-auto flex max-w-[1440px] flex-col gap-6 px-4 py-6 md:gap-8 md:px-8 md:py-10">

        {/* Punto de entrada al panel de admin — SOLO visible impersonando una cuenta
            interna (ver checkComunidadAdminAccess); el límite real es el guard del
            backend, esto es puramente para no mostrar el botón de más. */}
        {canSeeAdmin && !isPwaApp && (
          <Button
            variant="outline"
            size="sm"
            className="self-end border-[#C9BCE8] text-[#665090]"
            onClick={() => navigate("/comunidad/admin")}
          >
            <Settings size={15} className="mr-1.5" />
            Editar contenido de Comunidad
          </Button>
        )}

        {/* Banner de cumpleaños / aniversario — solo aparece el día exacto que corresponde
            (ver el useEffect que calcula `celebration` más arriba); el resto de los días no
            se renderiza nada acá. */}
        {celebration && (
          <div className="relative overflow-hidden rounded-[20px] bg-gradient-to-r from-[#8475B3] via-[#B679A8] to-[#F0A24A] px-5 py-6 md:px-10 md:py-8">
            <div className="relative flex flex-col items-center gap-4 text-center">
              {celebration.type === "birthday" ? (
                <p className="m-0 flex max-w-[740px] items-center justify-center gap-3.5 text-lg font-bold text-white md:text-2xl">
                  <span className="shrink-0 text-3xl leading-none">🎉</span>
                  ¡Feliz cumple! Hoy celebramos con vos este día tan especial. Te deseamos lo mejor
                  <span className="shrink-0 text-3xl leading-none">🎂</span>
                </p>
              ) : (
                <p className="m-0 flex max-w-[740px] items-center justify-center gap-3.5 text-lg font-bold text-white md:text-2xl">
                  <span className="shrink-0 text-3xl leading-none">🎉</span>
                  ¡Ya cumplís {celebration.years} {celebration.years === 1 ? "año" : "años"} con nosotros! Gracias por ser parte de Psi Mammoliti
                  <span className="shrink-0 text-3xl leading-none">💜</span>
                </p>
              )}
            </div>
          </div>
        )}

        {/* Encabezado */}
        <div className="flex flex-col items-center gap-4 text-center">
          <h1 className="m-0 text-3xl font-extrabold leading-tight md:text-4xl text-[#2D2049]">Comunidad 🤝</h1>
          <p className="m-0 max-w-[560px] text-base font-medium leading-relaxed text-[#4A3D73]">
            Un espacio para sentirte parte de algo más
          </p>
        </div>

        <div className="h-px bg-[#D8C9F0]" />

        {/* Zona de dos columnas: sidebar de novedades (izquierda) + contenido principal */}
        <div className="grid grid-cols-1 items-start gap-8 md:grid-cols-[280px_1fr] md:gap-9">

          {/* SIDEBAR: dos bloques de novedades, scroll propio e independiente */}
          <div className="flex flex-col gap-7 md:sticky md:top-6 md:max-h-[calc(100vh-48px)] md:overflow-y-auto md:pr-1">


            <div className="flex flex-col gap-3.5">
              <h2 className="m-0 text-sm font-bold uppercase tracking-wide text-[#4A3D73]">Novedades Psi Platform</h2>
              {loading ? (
                <div className="flex justify-center py-6"><CircularProgress /></div>
              ) : (
                <div className="rounded-2xl bg-[#2D2049] px-4">
                  {(() => {
                    const news = content?.psiPlatformNews ?? [];
                    if (news.length === 0) {
                      return (
                        <p className="m-0 py-4 text-[12.5px] text-[#B8A9DC]">
                          {contentError ? "No pudimos cargar las novedades. Probá de nuevo en un rato." : "Todavía no hay novedades cargadas."}
                        </p>
                      );
                    }
                    return news.map((n, i) => (
                      <button
                        key={n.id}
                        type="button"
                        onClick={() => navigate(`/comunidad/novedades/psi-platform?item=${n.id}`)}
                        className={`flex w-full items-start gap-2.5 py-3.5 text-left ${i < news.length - 1 ? "border-b border-white/10" : ""}`}
                      >
                        <div className="flex h-8.5 w-8.5 shrink-0 items-center justify-center rounded-lg bg-[#E6F381]/15">
                          <Sparkles size={16} className="text-[#E6F381]" />
                        </div>
                        <span className="text-[12.5px] font-semibold leading-snug text-white">{n.title}</span>
                      </button>
                    ));
                  })()}
                </div>
              )}
              <button
                type="button"
                onClick={() => navigate("/comunidad/novedades/psi-platform")}
                className="self-center text-xs font-semibold text-[#665090]"
              >
                Ver todas →
              </button>
            </div>

            <div className="h-px bg-[#D8C9F0]" />

            <div className="flex flex-col gap-3.5">
              <h2 className="m-0 text-sm font-bold uppercase tracking-wide text-[#4A3D73]">Institucional</h2>
              {loading ? (
                <div className="flex justify-center py-6"><CircularProgress /></div>
              ) : (
                <div
                  className={`rounded-2xl px-4 ${
                    hasFormacionesNews ? "border-2 border-[#6D57BC] bg-white/50 shadow-sm" : "border border-[#DCD0F2]"
                  }`}
                >
                  {(() => {
                    const news = content?.institutionalNews ?? [];
                    if (news.length === 0) {
                      return (
                        <p className="m-0 py-4 text-[12.5px] text-gray-400">
                          {contentError ? "No pudimos cargar las novedades. Probá de nuevo en un rato." : "Todavía no hay novedades cargadas."}
                        </p>
                      );
                    }
                    return news.map((n, i) => {
                      // Al llegar desde el pop-up de lanzamiento, además del calendario se resalta
                      // la novedad del ciclo de formaciones (contenido editable del panel de
                      // admin, sin id fijo: se reconoce por el título).
                      const highlighted = highlightFormaciones && /ciclo de formaciones/i.test(n.title);
                      return (
                        <button
                          key={n.id}
                          type="button"
                          onClick={() => navigate(`/comunidad/novedades/institucional?item=${n.id}`)}
                          className={`flex w-full items-start gap-2.5 py-3.5 text-left transition-colors duration-500 ${i < news.length - 1 ? "border-b border-[#F0EBFF]" : ""} ${
                            highlighted ? "-mx-2 w-[calc(100%+1rem)] animate-pulse rounded-xl bg-[#F5F1FF] px-2 ring-2 ring-[#8475B3]/60" : ""
                          }`}
                        >
                          <div className="flex h-8.5 w-8.5 shrink-0 items-center justify-center rounded-lg bg-[#EDE6FA]">
                            <MessageCircle size={16} className="text-[#4A3D73]" />
                          </div>
                          <div className="flex min-w-0 flex-col gap-0.5">
                            <span className="text-[12.5px] font-semibold leading-snug text-[#2D2049]">{n.title}</span>
                            {highlighted && (
                              <span className="mt-1.5 block rounded-lg bg-red-500 px-3 py-1.5 text-[13px] font-bold leading-snug text-white shadow-md">
                                Ingresá acá para más información!
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    });
                  })()}
                </div>
              )}
              <button
                type="button"
                onClick={() => navigate("/comunidad/novedades/institucional")}
                className="self-center text-xs font-semibold text-[#665090]"
              >
                Ver todas →
              </button>
            </div>

          </div>

          {/* COLUMNA PRINCIPAL */}
          <div className="flex min-w-0 flex-col gap-8">

            {/* Calendario — con filtros clicables y su propio scroll */}
            <div
              ref={calendarSectionRef}
              className={`-m-3 flex flex-col gap-3.5 rounded-[24px] p-3 transition-shadow duration-500 ${
                highlightFormaciones ? "animate-pulse bg-[#F5F1FF]/70 ring-4 ring-[#8475B3]/50" : ""
              }`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="m-0 text-xl font-bold text-[#2D2049]">Calendario — lo que se viene en Psi Mammoliti</h2>
                <div className="flex flex-wrap items-center gap-2">
                  {CALENDAR_TYPES.map((t) => {
                    const active = activeCalendarTypes.has(t.key);
                    return (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => toggleCalendarType(t.key)}
                        className={`flex items-center gap-1.5 rounded-full border py-1.5 pl-2 pr-3 text-xs font-semibold transition-colors ${
                          active ? "border-[#E3D9FF] bg-white text-[#4A3D73]" : "border-[#E3D9FF]/60 bg-transparent text-gray-400"
                        }`}
                      >
                        <span
                          className="inline-block h-2.5 w-2.5 rounded-full"
                          style={{ background: t.color, opacity: active ? 1 : 0.35 }}
                        />
                        {t.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="max-h-[230px] overflow-y-auto rounded-[20px] bg-[#F5F1FF] px-3 shadow-[0_6px_20px_rgba(109,87,188,0.08)] md:px-6">
                {loading ? (
                  <div className="flex justify-center py-6"><CircularProgress /></div>
                ) : filteredCalendarEvents.length === 0 ? (
                  <p className="m-0 py-4 text-center text-sm text-gray-400">
                    {calendarEvents.length === 0
                      ? "Pronto vamos a cargar los próximos encuentros."
                      : "No hay eventos para los filtros seleccionados."}
                  </p>
                ) : (
                  filteredCalendarEvents.map((ev, i) => {
                  // Los eventos con fecha se convierten a la timezone de la psicóloga que está
                  // mirando; los que todavía no tienen fecha se muestran como "Próximamente".
                  const viewer = ev.startsAt ? formatEventForViewer(ev.startsAt, viewerTimezone) : null;
                  const eventKey = ev.id;
                  const agendaLink = buildGoogleCalendarLink(ev);
                  return (
                    <div
                      key={eventKey}
                      className={`flex items-center gap-2 py-2.5 pl-3 pr-0 md:gap-4 md:pl-4 ${i < filteredCalendarEvents.length - 1 ? "border-b border-[#E3D9FF]" : ""}`}
                      style={{ borderLeft: `4px solid ${calendarBorderColor[ev.type]}` }}
                    >
                      <div className="w-14 shrink-0 text-center md:w-20">
                        {viewer ? (
                          <>
                            <div className="text-[10px] font-bold uppercase text-[#8475B3]">{viewer.month}</div>
                            <div className="text-base font-extrabold text-[#2D2049]">{viewer.day}</div>
                          </>
                        ) : (
                          <div className="whitespace-nowrap text-[9px] font-bold uppercase leading-tight text-[#8475B3]">
                            Próximamente
                          </div>
                        )}
                      </div>
                      <div className="flex min-w-0 flex-grow flex-col gap-0.5">
                        <div className="flex flex-wrap items-center gap-2.5">
                          <span className="text-[13.5px] font-semibold text-[#2D2049]">{ev.title}</span>
                          <span className="text-xs text-gray-500">{viewer ? viewer.meta : "Fecha a confirmar"}</span>
                        </div>
                        {ev.edition && (
                          <span className="text-[11px] font-semibold text-[#8475B3]">Edición {ev.edition}</span>
                        )}
                      </div>
                      {agendaLink && (
                        <a
                          href={agendaLink}
                          target="_blank"
                          rel="noreferrer"
                          // En formaciones: bloquea la agenda en ese horario (o avisa si ya hay una
                          // sesión), registra la inscripción y recién ahí abre Google Calendar.
                          onClick={(e) => handleAnotarme(e, ev, agendaLink)}
                          aria-disabled={enrollingEventId === ev.id}
                          className={`inline-flex h-8 shrink-0 items-center justify-center rounded-full border-2 border-[#6D57BC] bg-white px-4 text-sm font-bold text-[#6D57BC] shadow-sm transition-colors hover:bg-[#6D57BC] hover:text-white ${enrollingEventId === ev.id ? "pointer-events-none opacity-60" : ""}`}
                        >
                          {enrollingEventId === ev.id ? "Anotando…" : ev.type === "formacion" ? "Anotarme" : "Agendar"}
                        </a>
                      )}
                    </div>
                  );
                  })
                )}
              </div>
            </div>

            {/* Alerta de feriado — feriados reales del país de la psicóloga (ver el useEffect
                que arma `holidayMessage` más arriba); si no hay ninguno próximo, no se
                renderiza nada acá. */}
            {holidayMessage && (
              <div className="flex flex-wrap items-center gap-4 rounded-[20px] bg-white px-4 py-5 shadow-[0_6px_20px_rgba(109,87,188,0.08)] md:px-6">
                <AlertTriangle size={26} className="shrink-0 text-amber-500" />
                <div className="flex min-w-0 flex-grow flex-col gap-0.5 md:min-w-[240px]">
                  <span className="text-[13.5px] font-bold text-amber-700">{holidayMessage}</span>
                  <span className="text-xs leading-snug text-gray-500">¿Vas a trabajar? Organizate con tiempo y bloqueá tu agenda si lo necesitás.</span>
                </div>
                {!isPwaApp && (
                  <Button
                    className="bg-amber-600 hover:bg-amber-600/90"
                    onClick={handleBlockHoliday}
                    disabled={checkingHoliday}
                  >
                    {checkingHoliday ? "Revisando tu agenda…" : "Bloquear mi agenda"}
                  </Button>
                )}
              </div>
            )}

            <div className="h-px bg-[#D8C9F0]" />

            {/* Para tu práctica */}
            <div className="flex flex-col gap-4">
              <h2 className="m-0 text-lg font-bold text-[#2D2049]">Para tu práctica</h2>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div className="flex flex-col gap-3.5 rounded-[20px] bg-white p-4 shadow-[0_6px_20px_rgba(109,87,188,0.08)] md:p-6">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#ECFFD5]">
                      <Library size={18} className="text-[#4A7A1E]" />
                    </div>
                    <span className="text-[14.5px] font-bold text-[#2D2049]">Biblioteca Comunitaria</span>
                  </div>
                  <p className="m-0 text-xs leading-relaxed text-gray-500">Libros, formaciones y material compartido por colegas — de la comunidad, para la comunidad.</p>
                  <div className="mt-auto flex items-center justify-between gap-2.5">
                    <a
                      href={BIBLIOTECA_COMUNITARIA_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[13px] font-semibold text-[#665090] no-underline"
                    >
                      Explorar la biblioteca →
                    </a>
                    <a href={sugerirRecursoUrl} target="_blank" rel="noreferrer" className="text-xs text-gray-400 no-underline">
                      Sugerir un recurso
                    </a>
                  </div>
                </div>
                <div className="flex flex-col gap-3.5 rounded-[20px] bg-white p-4 shadow-[0_6px_20px_rgba(109,87,188,0.08)] md:p-6">
                  <div className="flex items-center gap-2.5">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#E9E0FC]">
                      <BookOpen size={18} className="text-[#665090]" />
                    </div>
                    <span className="text-[14.5px] font-bold text-[#2D2049]">Recursero</span>
                  </div>
                  <p className="m-0 text-xs leading-relaxed text-gray-500">Podcasts, guías y material de Psi Mammoliti para usar en sesión o entre sesiones.</p>
                  <div className="flex items-center gap-2.5 rounded-full border border-[#E3D9FF] bg-[#F5F1FF] px-4 py-2.5 opacity-60">
                    <Search size={17} className="text-[#8475B3]" />
                    <span className="text-[13px] text-gray-400">¿Buscás algún recurso para tus pacientes?</span>
                  </div>
                  <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-300">Próximamente</span>
                </div>
              </div>
            </div>

            {/* Newsletter — adelanto fijo de la última edición, siempre el mismo tamaño.
                El archivo completo (todas las ediciones anteriores) vive en otra pantalla.
                El título nunca lleva el nombre del mes (quedaría desactualizado apenas
                cambia la edición) — mientras no haya ninguna edición publicada, se muestra
                un estado "Próximamente" en vez de nada. */}
            {loading ? (
              <div className="flex justify-center py-6"><CircularProgress /></div>
            ) : (
              <div className="flex flex-col gap-4">
                <h2 className="m-0 text-lg font-bold text-[#2D2049]">Newsletter</h2>
                {content && content.newsletterEditions.length > 0 ? (
                  <div className="flex flex-col gap-3 rounded-[20px] bg-white p-4 shadow-[0_6px_20px_rgba(109,87,188,0.08)] md:p-6">
                    <span className="w-fit rounded-full bg-[#EDE6FA] px-3 py-0.5 text-xs font-semibold text-[#4A3D73]">
                      Edición #{content.newsletterEditions[0].editionNumber} · {content.newsletterEditions[0].month}
                    </span>
                    <span className="text-lg font-extrabold leading-snug text-[#2D2049]">
                      {content.newsletterEditions[0].theme}
                    </span>
                    <p className="m-0 line-clamp-3 whitespace-pre-line text-sm leading-relaxed text-[#4A3D73]">
                      {isRichHtml(content.newsletterEditions[0].body)
                        ? htmlToPlainText(content.newsletterEditions[0].body)
                        : content.newsletterEditions[0].body}
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate(`/comunidad/newsletter?edicion=${content.newsletterEditions[0].editionNumber}`)}
                      className="self-start text-[13px] font-semibold text-[#665090]"
                    >
                      Leer entero →
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 rounded-[20px] bg-white p-4 shadow-[0_6px_20px_rgba(109,87,188,0.08)] md:p-6">
                    <span className="text-2xl leading-none">💌</span>
                    <p className="m-0 text-sm font-medium text-[#8475B3]">
                      {contentError
                        ? "No pudimos cargar el newsletter. Probá de nuevo en un rato."
                        : "Ya estamos armando la primera edición — ¡prontito por acá!"}
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="h-px bg-[#D8C9F0]" />

            {/* Tu trayectoria en Psi Mammoliti */}
            <div className="flex flex-col gap-4">
              <h2 className="m-0 text-lg font-bold text-[#2D2049]">Tu trayectoria en Psi Mammoliti</h2>
              {trayectoriaLoading ? (
                <div className="flex justify-center rounded-[20px] bg-white p-4 shadow-[0_6px_20px_rgba(109,87,188,0.08)] md:p-6">
                  <CircularProgress />
                </div>
              ) : trayectoria ? (
                <div className="grid grid-cols-1 gap-5 rounded-[20px] bg-white p-4 shadow-[0_6px_20px_rgba(109,87,188,0.08)] sm:grid-cols-3 md:p-6">
                  <div className="flex flex-col gap-1">
                    <span className="text-2xl font-extrabold text-[#2D2049]">{formatCount(trayectoria.sessionsThisYear)}</span>
                    <span className="text-xs leading-snug text-gray-500">Sesiones brindadas este año</span>
                    <span className="text-[11.5px] font-semibold text-[#8475B3]">
                      {formatCount(trayectoria.sessionsTotal)} en total desde tu ingreso
                    </span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-2xl font-extrabold text-[#2D2049]">{formatCount(trayectoria.processesAccompanied)}</span>
                    <span className="text-xs leading-snug text-gray-500">Procesos acompañados</span>
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="text-2xl font-extrabold text-[#2D2049]">{tenure?.label ?? "—"}</span>
                    <span className="text-xs leading-snug text-gray-500">Desde tu ingreso a Psi Mammoliti</span>
                    {tenure?.almost && (
                      <span className="text-[11.5px] font-semibold text-[#8475B3]">{tenure.almost}</span>
                    )}
                  </div>
                </div>
              ) : (
                <div className="rounded-[20px] bg-white p-4 shadow-[0_6px_20px_rgba(109,87,188,0.08)] md:p-6">
                  <p className="m-0 text-sm text-gray-400">No pudimos cargar tu trayectoria. Probá de nuevo en un rato.</p>
                </div>
              )}
            </div>

            {/* Formaciones — SOLO para quien dictó al menos una, y siempre visible para ella:
                sus certificados de dictado y, si además participó de la formación del mes, su
                certificado de participación y "Calificá" (que para el resto van más abajo). Si
                tiene de los dos, un subtítulo chiquito separa las que dictó de las que hizo. */}
            {dictoFormaciones && (
              <div className="flex flex-col gap-4">
                <h2 className="m-0 text-lg font-bold text-[#2D2049]">Formaciones</h2>
                {(participatedInFormacion || tusCertificadosCard) && (
                  <h3 className="m-0 text-sm font-semibold text-[#4A3D73]">Las que dictaste</h3>
                )}
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-[7fr_4fr]">{renderDictadasCards()}</div>
                {(participatedInFormacion || tusCertificadosCard) && (
                  <h3 className="m-0 mt-2 text-sm font-semibold text-[#4A3D73]">A las que asististe</h3>
                )}
                {participatedInFormacion ? (
                  renderParticipacionGrid()
                ) : (
                  tusCertificadosCard && (
                    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">{tusCertificadosCard}</div>
                  )
                )}
              </div>
            )}

            {/* Hacé crecer la comunidad — siempre visible, para todas. ("Generá tu banner de
                LinkedIn" queda oculto hasta definir qué banners se usan — backlog.) */}
            <div className="flex flex-col gap-4">
              <h2 className="m-0 text-lg font-bold text-[#2D2049]">Hacé crecer la comunidad</h2>
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div className="flex flex-col gap-3.5 rounded-[20px] bg-white p-4 shadow-[0_6px_20px_rgba(109,87,188,0.08)] md:p-6">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E9E0FC]">
                    <UserPlus size={20} className="text-[#665090]" />
                  </div>
                  <div className="flex flex-grow flex-col gap-1">
                    <span className="text-xs font-bold uppercase tracking-wide text-[#8475B3]">Referí</span>
                    <span className="text-[15px] font-semibold leading-snug text-[#2D2049]">Recomendá a un/a colega</span>
                    <span className="text-[13px] text-gray-500">Estamos sumando gente al equipo</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="self-start border-[#C9BCE8] text-[#665090]"
                    onClick={() => setReferralOpen(true)}
                  >
                    Referir
                  </Button>
                </div>
                <div className="flex flex-col gap-3.5 rounded-[20px] bg-white p-4 shadow-[0_6px_20px_rgba(109,87,188,0.08)] md:p-6">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E9E0FC]">
                    <MessageCircle size={20} className="text-[#665090]" />
                  </div>
                  <div className="flex flex-grow flex-col gap-1">
                    <span className="text-xs font-bold uppercase tracking-wide text-[#8475B3]">Tu voz</span>
                    <span className="text-[15px] font-semibold leading-snug text-[#2D2049]">Contanos lo que se te ocurra</span>
                    <span className="text-[13px] text-gray-500">Ideas, propuestas o cualquier cosa que creas que puede sumarle a la comunidad. Todo lo que nos compartas nos ayuda a crecer</span>
                  </div>
                  <a
                    href={proponerTemaUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex h-8 w-fit shrink-0 items-center justify-center rounded-full border border-[#C9BCE8] px-3 text-sm font-medium text-[#665090] transition-colors hover:bg-[#665090]/5"
                  >
                    Escribinos
                  </a>
                </div>
              </div>
            </div>

            <div className="h-px bg-[#D8C9F0]" />

            {/* Tu participación en la formación — SOLO para quien se anotó y se quedó en la
                formación (la que recibe certificado y puede calificar). A quien no participó no
                se le muestra nada: ni la sección ni el separador. "Calificar" se habilita entre
                el fin de la 1ª edición del mes y el cierre de ese mes (ver
                currentFormacion.calificaFrom/Until, que salen del calendario cargado en el panel) — antes de eso, muestra "Próximamente". */}
            {participatedInFormacion && !dictoFormaciones && (
              <>
                <div className="flex flex-col gap-4">
                  <h2 className="m-0 text-lg font-bold text-[#2D2049]">Tu participación en la formación</h2>
                  {renderParticipacionGrid()}
                </div>

                <div className="h-px bg-[#D8C9F0]" />
              </>
            )}

            {/* Quien no participó de la formación del mes pero tiene certificados anteriores
                igual tiene dónde encontrarlos. */}
            {!participatedInFormacion && !dictoFormaciones && misCertificados.length > 0 && (
              <>
                <div className="flex flex-col gap-4">
                  <h2 className="m-0 text-lg font-bold text-[#2D2049]">Tus certificados</h2>
                  <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">{tusCertificadosCard}</div>
                </div>

                <div className="h-px bg-[#D8C9F0]" />
              </>
            )}

            {/* Espacio de intercambio entre colegas — dentro de la columna principal a
                propósito: tiene que tener el mismo ancho que el resto del contenido, no
                extenderse debajo del sidebar de novedades. */}
            <div className="flex w-full flex-wrap items-center justify-between gap-5 rounded-[20px] bg-[#665090] px-5 py-5 md:px-7 md:py-6">
              <div className="flex items-center gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15">
                  <Users size={22} className="text-white" />
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-[15.5px] font-bold text-white">Espacio de intercambio entre colegas</span>
                  <span className="text-[13px] text-[#E4DCF5]">Consultá dudas, compartí ideas y charlá con el equipo</span>
                </div>
              </div>
              {/* Todavía no existe el espacio (sería tipo Slack) — "Próximamente" hasta que esté. */}
              <span className="rounded-full bg-white/15 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white/80">
                Próximamente
              </span>
            </div>

          </div>
        </div>

      </div>

      {/* Compartir el certificado en LinkedIn: publicación con texto sugerido (el archivo lo
          adjunta ella). Sin "agregar al perfil" a propósito: el certificado no tiene validez
          formal como para figurar en "Licencias y certificaciones". */}
      <Dialog
        open={linkedinOpen}
        onOpenChange={setLinkedinOpen}
      >
        <DialogContent className="flex flex-col gap-4 p-6">
          <DialogHeader className="mb-0">
            <DialogTitle className="m-0 text-[#2D2049]">Compartí tu logro en LinkedIn 🎓</DialogTitle>
          </DialogHeader>
          {certificado && (
            <>
              <div className="flex flex-col gap-2 rounded-xl border border-[#E3D9FF] p-4">
                <span className="text-[13px] text-gray-500">
                  Se abre una publicación con un texto sugerido (lo podés cambiar). Adjuntale tu certificado:
                  descargalo primero si todavía no lo tenés.
                </span>
                <div className="flex flex-wrap items-center gap-2">
                  <a
                    href={linkedinPostUrl(certificado)}
                    target="_blank"
                    rel="noopener noreferrer"
                    // Métricas de Actividad: abre la publicación en LinkedIn.
                    onClick={() => activityTracker.trackAction("comunidad_certificado_linkedin")}
                    className="inline-flex h-8 items-center justify-center rounded-full bg-[#665090] px-4 text-sm font-medium text-white hover:bg-[#665090]/90"
                  >
                    Publicar en LinkedIn
                  </a>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-[#C9BCE8] text-[#665090]"
                    disabled={downloadingKey !== null}
                    onClick={() => handleDownloadCertificado(certificado, "imagen")}
                  >
                    {downloadingKey === certificado.formacionKey ? "Armando…" : "Descargar certificado"}
                  </Button>
                </div>
              </div>
              <span className="text-xs text-gray-400">
                Si en este navegador no tenés abierto LinkedIn, te va a pedir que entres con tu cuenta.
              </span>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Aviso de "Bloquear mi agenda" (feriado): ese día ya tiene sesiones agendadas. No se
          cancela ni modifica ninguna sesión. Mismo cartel que el aviso de feriados de la Agenda. */}
      <HolidaySessionsDialog info={holidaySessions} timezone={viewerTimezone} onClose={() => setHolidaySessions(null)} />

      {/* Aviso de "Anotarme": ya tiene una sesión agendada en el horario de la formación */}
      <Dialog open={sessionConflict !== null} onOpenChange={(open) => { if (!open) setSessionConflict(null); }}>
        <DialogContent className="flex flex-col gap-4 p-6">
          <DialogHeader className="mb-0">
            <DialogTitle className="m-0 text-[#2D2049]">Tenés una sesión agendada a esa hora</DialogTitle>
          </DialogHeader>
          <p className="m-0 text-sm leading-relaxed text-[#4A3D73]">
            No pudimos anotarte a «{sessionConflict?.title}» porque el {sessionConflict?.date} a las {sessionConflict?.time} hs
            ya tenés una sesión agendada.
          </p>
          <Button className="self-end bg-[#665090] text-white hover:bg-[#665090]/90" onClick={() => setSessionConflict(null)}>
            Entendido
          </Button>
        </DialogContent>
      </Dialog>

      {/* "Ver todos" de Tus certificados */}
      <Dialog open={todosOpen} onOpenChange={setTodosOpen}>
        <DialogContent className="flex max-h-[85vh] flex-col gap-4 overflow-y-auto p-6">
          <DialogHeader className="mb-0">
            <DialogTitle className="m-0 text-[#2D2049]">Tus certificados 🎓</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            {misCertificados.map((c) => (
              <div key={c.formacionKey} className="flex items-center gap-4 rounded-xl border border-[#E3D9FF] p-3">
                <CertificadoMiniatura certificado={c} className="!w-32 shrink-0" />
                <div className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="line-clamp-2 text-sm font-semibold leading-snug text-[#2D2049]">{c.formacionTitle}</span>
                  {formatCertificadoDate(c.startsAt) && (
                    <span className="text-xs text-gray-500">{formatCertificadoDate(c.startsAt)}</span>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-1 self-start border-[#C9BCE8] text-[#665090]"
                    disabled={downloadingKey !== null}
                    onClick={() => handleDownloadCertificado(c)}
                  >
                    {downloadingKey === c.formacionKey ? "Armando…" : "Descargar"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Pop-up de "Recomendá a un/a colega" */}
      <Dialog
        open={referralOpen}
        onOpenChange={(open) => {
          setReferralOpen(open);
          if (!open) setReferralCopied(false);
        }}
      >
        <DialogContent className="flex flex-col gap-4 p-6">
          <DialogHeader className="mb-0">
            <DialogTitle className="m-0 text-[#2D2049]">Recomendá a un/a colega ✨</DialogTitle>
          </DialogHeader>
          <p className="m-0 text-sm leading-relaxed text-[#4A3D73]">
            ¡Holaaa! 💜 Estamos muy contentos de que quieras recomendarnos como el futuro espacio de trabajo de alguien que
            conozcas. Para invitarle a participar del proceso, copiá este mensaje y enviaselo:
          </p>
          <div className="whitespace-pre-line rounded-xl border border-[#E3D9FF] bg-[#F5F1FF] p-4 text-sm leading-relaxed text-[#2D2049]">
            {REFERRAL_MESSAGE}
          </div>
          <Button className="self-end bg-[#665090] text-white hover:bg-[#665090]/90" onClick={copyReferralMessage}>
            {referralCopied ? <Check size={15} className="mr-1.5" /> : <Copy size={15} className="mr-1.5" />}
            {referralCopied ? "¡Copiado!" : "Copiar mensaje"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComunidadContent;
